import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyEmail, resendVerification } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './EmailVerification.css';

const EmailVerification = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [verificationCode, setVerificationCode] = useState(searchParams.get('code') || '');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Auto-verify if code is in URL
    const codeFromURL = searchParams.get('code');
    if (codeFromURL) {
      handleVerify(codeFromURL);
    }
  }, [searchParams, isAuthenticated, navigate]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleVerify = async (code = verificationCode) => {
    if (!code) {
      setError(t('verification.errors.emptyCode'));
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await verifyEmail(code);
      setMessage(t('verification.success'));
      
      // Update user data in localStorage with verified user from backend
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }
      
      // Redirect after short delay
      setTimeout(() => {
        window.location.href = '/dashboard'; // Force full page reload to update auth context
      }, 1500);
    } catch (err) {
      setError(err.message || t('verification.errors.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!user?.email) {
      setError(t('verification.errors.noUser'));
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      await resendVerification(user.email);
      setMessage(t('verification.resent'));
      setCountdown(180); // 3 minutes
    } catch (err) {
      if (err.message.includes('please wait')) {
        setError(err.message);
        // Extract seconds from error message
        const match = err.message.match(/(\d+) seconds/);
        if (match) {
          setCountdown(parseInt(match[1]));
        }
      } else {
        setError(err.message || t('verification.errors.sendFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    // Refresh user data from server
    setMessage(t('verification.checking'));
    // TODO: Implement check verification status
    setTimeout(() => {
      setMessage(t('verification.errors.refresh'));
    }, 1000);
  };

  return (
    <div className="verification-page">
      <div className="verification-orb verification-orb-1"></div>
      <div className="verification-orb verification-orb-2"></div>
      <div className="verification-orb verification-orb-3"></div>

      <div className="page-language-switcher">
        <LanguageSwitcher />
      </div>

      <div className="verification-container">
        <div className="verification-card">
          <div className="verification-header">
            <div className="icon-circle">
              <span className="icon">📧</span>
            </div>
            <h1>{t('verification.title')}</h1>
            <p className="subtitle">
              {t('verification.subtitle')}
            </p>
          </div>

          {user && !user.email_verified && (
            <div className="info-box">
              <p>
                <strong>{user.email}</strong> {t('verification.instruction')}
              </p>
            </div>
          )}

          {message && (
            <div className="success-message">
              {message}
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="verification-form">
            <div className="form-group">
              <label>{t('verification.codeLabel')}</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder={t('verification.codePlaceholder')}
                disabled={isLoading}
              />
            </div>

            <button
              className="verify-btn"
              onClick={() => handleVerify()}
              disabled={isLoading || !verificationCode}
            >
              {isLoading ? t('verification.verifying') : t('verification.verifyBtn')}
            </button>
          </div>

          <div className="divider">
            <span>{t('verification.or')}</span>
          </div>

          <div className="resend-section">
            <p>{t('verification.didntReceive')}</p>
            <button
              className="resend-btn"
              onClick={handleResend}
              disabled={isLoading || countdown > 0}
            >
              {countdown > 0 
                ? `${t('verification.resendBtn')} (${countdown}s)` 
                : t('verification.resendBtn')
              }
            </button>
          </div>

          <div className="check-section">
            <button
              className="check-btn"
              onClick={handleCheckVerification}
              disabled={isLoading}
            >
              {t('verification.checkBtn')}
            </button>
          </div>

          <div className="back-link">
            <button onClick={() => navigate('/dashboard')}>
              ← {t('verification.backToDashboard')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
